import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@clerk/clerk-react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { BACKEND_URL } from "@/constants/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  LucidePlus,
  LucideUser,
  LucideShare2,
  LucideChevronLeft,
  LucideDownload,
  LucideSettings2,
} from "lucide-react";
import { REPORT_FIELD_LABELS } from "@/reports/shared_utils/reportFieldLabels";
import { ExportModal } from "@/components/modals/ExportModal";
import { ShareClassModal } from "@/components/modals/ShareClassModal";
import { AddStudentModal } from "@/components/modals/AddStudentModal";
import { DeleteReportModal } from "@/components/modals/DeleteReportModal";
import { PageHeader } from "@/layouts/PageHeader";
import { useSubscription } from "@/context/SubscriptionContext";
import { StudentReportList } from "@/components/entities/StudentReportList";

const REPORTS_PAGE_SIZE = 10;

export const OwnedClass = () => {
  const { reportGroupId } = useParams();
  const { getToken } = useAuth();
  const navigate = useNavigate();

  const [classData, setClassData] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [showAddStudentModal, setShowAddStudentModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [allStudentsForModal, setAllStudentsForModal] = useState([]);
  const [loadingModalStudents, setLoadingModalStudents] = useState(false);

  const [reportsPage, setReportsPage] = useState(0);
  const [sortField, setSortField] = useState("oen");
  const [sortOrder, setSortOrder] = useState("asc");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [totalReports, setTotalReports] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [showExportModal, setShowExportModal] = useState(false);
  const [allReportsForExport, setAllReportsForExport] = useState([]);
  const [cardToDelete, setCardToDelete] = useState(null);

  const { reportsRemaining, loading: subLoading } = useSubscription();
  const atLimit = !subLoading && reportsRemaining === 0;

  const fetchClass = useCallback(async () => {
    try {
      const token = await getToken();
      const params = new URLSearchParams({
        sort: sortField,
        order: sortOrder,
        search: debouncedSearch,
        page: reportsPage,
        limit: REPORTS_PAGE_SIZE,
      });
      const res = await fetch(
        `${BACKEND_URL}api/report-groups/${reportGroupId}?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error("Failed to fetch class");
      const data = await res.json();
      setClassData(data);
      setReports(data.reports || []);
      setTotalReports(data.totalReports ?? 0);
      setTotalPages(data.totalPages ?? 1);
      setFetchError(null);
    } catch (err) {
      console.error("Error fetching class:", err);
      setFetchError("Could not load this class. Please refresh the page.");
    } finally {
      setLoading(false);
    }
  }, [reportGroupId, sortField, sortOrder, debouncedSearch, reportsPage, getToken]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset to page 0 when sort or search changes
  useEffect(() => {
    setReportsPage(0);
  }, [sortField, sortOrder, debouncedSearch]);

  // Fetch whenever group, sort, search, or page changes
  useEffect(() => {
    fetchClass();
  }, [fetchClass]);

  const handleCreate = async (studentName, oen, applyDefaults) => {
    const token = await getToken();
    const body = { studentName, applyDefaults };
    if (oen) body.oen = oen;
    const res = await fetch(
      `${BACKEND_URL}api/report-groups/${reportGroupId}/reports`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      }
    );
    if (res.status === 403) {
      throw new Error("Report card quota exceeded. Please upgrade your plan.");
    }
    if (!res.ok) throw new Error("Failed to create report card. Please try again.");
    const newCard = await res.json();
    navigate(`/classes/${reportGroupId}/reports/${newCard._id}`);
  };

  const openAddStudentModal = async () => {
    setShowAddStudentModal(true);
    setLoadingModalStudents(true);
    try {
      const token = await getToken();
      const params = new URLSearchParams({ sort: sortField, order: sortOrder, limit: 0 });
      const res = await fetch(
        `${BACKEND_URL}api/report-groups/${reportGroupId}?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error();
      const data = await res.json();
      setAllStudentsForModal(data.reports || []);
    } catch {
      setAllStudentsForModal(reports);
    } finally {
      setLoadingModalStudents(false);
    }
  };

  const handleDelete = (card) => {
    setCardToDelete(card);
  };

  const confirmDelete = async () => {
    const card = cardToDelete;
    setCardToDelete(null);
    try {
      const token = await getToken();
      await fetch(`${BACKEND_URL}api/reports/${card._id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      setReports((prev) => prev.filter((r) => r._id !== card._id));
    } catch (err) {
      console.error("Error deleting report card:", err);
      toast.error("Failed to delete report card. Please try again.");
    }
  };

  const fieldLabels = REPORT_FIELD_LABELS[classData?.reportType] ?? [];

  const openExportModal = async () => {
    try {
      const token = await getToken();
      const params = new URLSearchParams({
        sort: sortField,
        order: sortOrder,
        limit: 0,
      });
      const res = await fetch(
        `${BACKEND_URL}api/report-groups/${reportGroupId}?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error("Failed to fetch reports");
      const data = await res.json();
      setAllReportsForExport(data.reports || []);
    } catch {
      setAllReportsForExport(reports);
    }
    setShowExportModal(true);
  };

  if (fetchError) {
    return (
      <div className="px-10 py-9 max-w-[960px] mx-auto max-md:px-4 max-md:pt-5 max-md:pb-6">
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {fetchError}
        </div>
      </div>
    );
  }

  return (
    <div className="px-10 py-9 max-w-[960px] mx-auto max-md:px-4 max-md:pt-5 max-md:pb-6">
      <Link
        to="/classes"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6"
      >
        <LucideChevronLeft size={14} />
        My Classes
      </Link>

      <PageHeader
        title={
          [
            classData?.grade ? `Grade ${classData.grade}` : null,
            classData?.schoolYear || null,
          ]
            .filter(Boolean)
            .join(" · ") ||
          classData?.name ||
          "Class"
        }
        subtitle={classData?.name}
        action={
          <div className="flex items-center gap-3 flex-wrap">
            {atLimit && (
              <span className="text-sm text-destructive">
                Report card limit reached.{" "}
                <Link to="/billing" className="underline">
                  Upgrade
                </Link>
              </span>
            )}
            {reports.length > 0 && (
              <Button variant="outline" onClick={openExportModal}>
                <LucideDownload className="mr-2" size={16} />
                Export
              </Button>
            )}
            {fieldLabels.length > 0 && (
              <Button
                variant="outline"
                onClick={() => navigate(`/classes/${reportGroupId}/defaults`)}
              >
                <LucideSettings2 className="mr-2" size={16} />
                Report Defaults
              </Button>
            )}
            <Button variant="outline" onClick={() => setShowShareModal(true)}>
              <LucideShare2 className="mr-2" size={16} />
              Share
            </Button>
            <Button onClick={openAddStudentModal} disabled={atLimit}>
              <LucidePlus className="mr-2" size={16} />
              New Report Card
            </Button>
          </div>
        }
      />

      {/* Search + sort controls */}
      {(totalReports > 0 || searchQuery) && (
        <div className="flex flex-wrap gap-2 mb-4">
          <Input
            placeholder="Search by name or OEN..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="max-w-xs"
          />
          <select
            value={`${sortField}:${sortOrder}`}
            onChange={(e) => {
              const [field, ord] = e.target.value.split(":");
              setSortField(field);
              setSortOrder(ord);
            }}
            className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 text-foreground"
          >
            <option value="oen:asc">OEN (0-9)</option>
            <option value="oen:desc">OEN (9-0)</option>
            <option value="name:asc">Name (A-Z)</option>
            <option value="name:desc">Name (Z-A)</option>
            <option value="updatedAt:desc">Recently edited</option>
            <option value="updatedAt:asc">Oldest edited</option>
          </select>
        </div>
      )}

      {loading ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-[60px] bg-muted rounded-lg animate-pulse" />
          ))}
        </div>
      ) : reports.length === 0 && !searchQuery ? (
        <div className="text-center py-16 text-muted-foreground">
          <LucideUser size={40} className="mx-auto mb-3 opacity-40" />
          <p>No report cards yet. Add students to get started.</p>
        </div>
      ) : reports.length === 0 && searchQuery ? (
        <div className="text-center py-12 text-muted-foreground">
          <p>No students match &quot;{searchQuery}&quot;.</p>
        </div>
      ) : (
        <StudentReportList
          reports={reports}
          reportGroupId={reportGroupId}
          totalPages={totalPages}
          currentPage={reportsPage}
          onPageChange={setReportsPage}
          onDelete={handleDelete}
        />
      )}

      <AddStudentModal
        open={showAddStudentModal}
        onClose={() => setShowAddStudentModal(false)}
        onSubmit={handleCreate}
        hasDefaults={Object.keys(classData?.reportDefaults ?? {}).length > 0}
        existingReports={allStudentsForModal}
        loadingReports={loadingModalStudents}
      />

      <ExportModal
        open={showExportModal}
        onClose={() => setShowExportModal(false)}
        reports={allReportsForExport}
        classData={classData}
      />

      <ShareClassModal
        open={showShareModal}
        onClose={() => setShowShareModal(false)}
        reportGroupId={reportGroupId}
        classData={classData}
        onClassDataChange={setClassData}
        onRefresh={fetchClass}
      />

      <DeleteReportModal
        open={!!cardToDelete}
        onClose={() => setCardToDelete(null)}
        onConfirm={confirmDelete}
        studentName={cardToDelete?.studentName}
        oen={cardToDelete?.data?.oen}
      />
    </div>
  );
};
